import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { useState, useMemo } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useServerFn } from "@tanstack/react-start";
import { supabase } from "@/integrations/supabase/client";
import { suggestPutawayLocation, receiveSerials } from "@/lib/fulfillment.functions";
import {
  getAsn,
  listAsnLines,
  listAsnSkus,
  listAsnLocations,
  addAsnLine,
  recordReceipt,
  listReceiptPhotos,
  recordReceiptPhoto,
  deleteReceiptPhoto,
  recordReceivingVariance,
  closeAsn,
  deleteAsn,
} from "@/lib/receiving.functions";
import { runServerFn } from "@/lib/server-fn";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import { StatusPill, statusTone } from "@/components/ui/status-pill";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Plus, Loader2, PackageCheck, Camera, Trash2, Lightbulb, Hash, AlertTriangle } from "lucide-react";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";

export const Route = createFileRoute("/_authenticated/app/receiving/$asnId")({ component: AsnDetailPage });

function AsnDetailPage() {
  const { asnId } = Route.useParams();
  const qc = useQueryClient();
  const navigate = useNavigate();
  const suggestPutawayFn = useServerFn(suggestPutawayLocation);
  const receiveSerialsFn = useServerFn(receiveSerials);
  const getAsnFn = useServerFn(getAsn);
  const listLinesFn = useServerFn(listAsnLines);
  const listSkusFn = useServerFn(listAsnSkus);
  const listLocationsFn = useServerFn(listAsnLocations);
  const addAsnLineFn = useServerFn(addAsnLine);
  const recordReceiptFn = useServerFn(recordReceipt);
  const recordVarianceFn = useServerFn(recordReceivingVariance);
  const closeAsnFn = useServerFn(closeAsn);
  const deleteAsnFn = useServerFn(deleteAsn);
  const [closing, setClosing] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const onDeleteAsn = async () => {
    if (!asn) return;
    if (!confirm(`Delete ASN ${asn.reference}? This cannot be undone. Only allowed when no receipts have been recorded.`)) return;
    setDeleting(true);
    try {
      await runServerFn(() => deleteAsnFn({ data: { asnId } }), "Delete ASN");
      toast.success("ASN deleted");
      qc.invalidateQueries({ queryKey: ["asns"] });
      navigate({ to: "/app/receiving" });
    } catch { /* toast surfaced */ } finally { setDeleting(false); }
  };
  const onCloseAsn = async () => {
    if (!asn) return;
    if (!confirm("Close this ASN? No further receipts can be recorded against it.")) return;
    setClosing(true);
    try {
      const res = await closeAsnFn({ data: { asnId } });
      toast.success(
        res.open_putaways_remaining
          ? `ASN closed. ${res.open_putaways_remaining} open putaway task(s) remain.`
          : "ASN closed.",
      );
      qc.invalidateQueries({ queryKey: ["asn", asnId] });
    } catch (e: any) {
      toast.error(e?.message ?? "Failed to close ASN");
    } finally {
      setClosing(false);
    }
  };
  const { data: asn } = useQuery({
    queryKey: ["asn", asnId],
    queryFn: () => getAsnFn({ data: { asnId } }),
  });
  const { data: lines, refetch: refetchLines } = useQuery({
    queryKey: ["asn-lines", asnId],
    queryFn: () => listLinesFn({ data: { asnId } }),
  });
  const { data: skus } = useQuery({
    queryKey: ["skus-for-asn", asn?.customer_id], enabled: !!asn?.customer_id,
    queryFn: () => listSkusFn({ data: { customerId: asn!.customer_id } }),
  });
  const { data: locations } = useQuery({
    queryKey: ["locations-for-asn", asn?.facility_id], enabled: !!asn?.facility_id,
    queryFn: () => listLocationsFn({ data: { facilityId: asn!.facility_id } }),
  });
  const skuLabel = (id: string) => { const s = skus?.find((x) => x.id === id); return s ? `${s.sku_code} — ${s.name}` : "—"; };
  const [addOpen, setAddOpen] = useState(false);
  const [skuId, setSkuId] = useState(""); const [qty, setQty] = useState(""); const [lot, setLot] = useState(""); const [adding, setAdding] = useState(false);
  const addLine = async (e: React.FormEvent) => {
    e.preventDefault();
    const n = parseInt(qty, 10);
    if (!skuId || !n || n <= 0) return toast.error("Pick a SKU and a positive quantity");
    setAdding(true);
    try {
      await runServerFn(
        () => addAsnLineFn({ data: { asnId, skuId, qty: n, lot: lot.trim() || null } }),
        "Add line",
      );
    } catch { setAdding(false); return; }
    setAdding(false);
    toast.success("Line added"); setAddOpen(false); setSkuId(""); setQty(""); setLot(""); refetchLines();
  };
  const [recvFor, setRecvFor] = useState<string | null>(null);
  const [recvQty, setRecvQty] = useState(""); const [recvLoc, setRecvLoc] = useState("");
  const [recvExpiry, setRecvExpiry] = useState(""); const [receiving, setReceiving] = useState(false);
  const [recvSerials, setRecvSerials] = useState("");
  // Variance capture state
  const [varianceFor, setVarianceFor] = useState<string | null>(null);
  const [varianceQty, setVarianceQty] = useState("");
  const [varianceReason, setVarianceReason] = useState<"receiving_overage" | "receiving_shortage">("receiving_shortage");
  const [varianceNotes, setVarianceNotes] = useState("");
  const [savingVariance, setSavingVariance] = useState(false);
  const varianceLine = useMemo(() => lines?.find((l) => l.id === varianceFor) ?? null, [lines, varianceFor]);

  const saveVariance = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!varianceLine) return;
    const n = parseInt(varianceQty, 10);
    if (!n || n <= 0) return toast.error("Enter a positive quantity");
    if (!varianceNotes.trim()) return toast.error("Notes are required to explain the variance");
    setSavingVariance(true);
    try {
      await runServerFn(
        () => recordVarianceFn({ data: {
          asnLineId: varianceLine.id,
          variance: n,
          reason: varianceReason,
          notes: varianceNotes.trim(),
        } }),
        "Record variance",
      );
      toast.success(`${varianceReason === "receiving_overage" ? "Overage" : "Shortage"} of ${n} recorded${n >= 10 ? " (pending supervisor approval)" : ""}`);
      setVarianceFor(null); setVarianceQty(""); setVarianceNotes("");
      qc.invalidateQueries({ queryKey: ["asn-lines", asnId] });
    } catch {
      // toast surfaced by runServerFn
    } finally { setSavingVariance(false); }
  };

  const lineToReceive = useMemo(() => lines?.find((l) => l.id === recvFor) ?? null, [lines, recvFor]);
  const lineSku = useMemo(() => skus?.find((s) => s.id === lineToReceive?.sku_id) ?? null, [skus, lineToReceive]);
  const tracksSerials = !!lineSku?.track_serials;

  // Putaway suggestion via RPC: rules → history → default storage slot
  const { data: suggestedLoc } = useQuery({
    queryKey: ["putaway-suggest", asn?.tenant_id, asn?.facility_id, lineToReceive?.sku_id],
    enabled: !!asn && !!lineToReceive,
    queryFn: () => suggestPutawayFn({
      data: {
        tenantId: asn!.tenant_id,
        facilityId: asn!.facility_id,
        skuId: lineToReceive!.sku_id,
      },
    }),
  });
  // When a suggestion exists and user hasn't picked a location yet, prefill
  useMemo(() => {
    if (recvFor && suggestedLoc?.location_id && !recvLoc) setRecvLoc(suggestedLoc.location_id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [recvFor, suggestedLoc?.location_id]);

  const receive = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!asn || !lineToReceive) return;
    const n = parseInt(recvQty, 10);
    if (!recvLoc || !n || n <= 0) return toast.error("Pick a location and a positive quantity");
    setReceiving(true);
    const serialList = tracksSerials
      ? recvSerials.split(/[\s,]+/).map((s) => s.trim()).filter(Boolean)
      : [];
    if (tracksSerials && serialList.length !== n) {
      setReceiving(false);
      return toast.error(`This SKU is serial-tracked. Provide exactly ${n} unique serials (got ${serialList.length}).`);
    }
    let rl: { receiptLineId: string } | null = null;
    try {
      const res = await runServerFn(
        () => recordReceiptFn({
          data: {
            asnId: asn.id,
            asnLineId: lineToReceive.id,
            locationId: recvLoc,
            qty: n,
            expiryDate: recvExpiry || null,
          },
        }),
        "Record receipt",
      );
      rl = { receiptLineId: res.receiptLineId };
    } catch { setReceiving(false); return; }
    if (tracksSerials && rl) {
      try {
        const r = await runServerFn(
          () => receiveSerialsFn({ data: { receiptLineId: rl!.receiptLineId, serials: serialList } }),
          "Receive serials",
        );
        if (r.skipped > 0) toast.warning(`${r.skipped} duplicate serial(s) skipped`);
      } catch {
        setReceiving(false);
        return;
      }
    }
    setReceiving(false);
    toast.success("Received"); setRecvFor(null); setRecvQty(""); setRecvLoc(""); setRecvExpiry(""); setRecvSerials("");
    qc.invalidateQueries({ queryKey: ["asn-lines", asnId] });
    qc.invalidateQueries({ queryKey: ["asn", asnId] });
    qc.invalidateQueries({ queryKey: ["balances"] });
  };
  if (!asn) return <div className="p-4 sm:p-6 text-muted-foreground">Loading…</div>;
  return (
    <div className="p-4 sm:p-6 space-y-4">
      <Card>
        <CardHeader className="flex flex-row items-start justify-between">
          <div>
            <CardTitle className="text-xl">{asn.reference}</CardTitle>
            <p className="text-sm text-muted-foreground mt-1">{asn.carrier ? `${asn.carrier} · ` : ""}{new Date(asn.expected_at ?? Date.now()).toLocaleString()}</p>
          </div>
          <div className="flex items-center gap-2">
            <StatusPill tone={statusTone(asn.status)} dot>{asn.status}</StatusPill>
            {asn.status !== "closed" && asn.status !== "cancelled" && (
              <Button size="sm" variant="outline" onClick={onCloseAsn} disabled={closing}>
                {closing && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
                Close ASN
              </Button>
            )}
            {asn.status === "expected" && (
              <Button size="sm" variant="outline" onClick={onDeleteAsn} disabled={deleting} className="text-destructive hover:text-destructive">
                {deleting ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Trash2 className="h-4 w-4 mr-2" />}
                Delete
              </Button>
            )}
          </div>
        </CardHeader>
        <CardContent>
          <div className="flex items-center justify-between">
            <h3 className="text-sm font-medium">Lines</h3>
            <Dialog open={addOpen} onOpenChange={setAddOpen}>
              <DialogTrigger asChild><Button size="sm" variant="outline"><Plus className="h-4 w-4 mr-2" />Add line</Button></DialogTrigger>
              <DialogContent>
                <DialogHeader><DialogTitle>Add ASN line</DialogTitle></DialogHeader>
                <form onSubmit={addLine} className="space-y-4">
                  <div className="space-y-2"><Label>SKU</Label>
                    <Select value={skuId} onValueChange={setSkuId}>
                      <SelectTrigger><SelectValue placeholder="Select SKU" /></SelectTrigger>
                      <SelectContent>{skus?.map((s) => <SelectItem key={s.id} value={s.id}>{s.sku_code} — {s.name}</SelectItem>)}</SelectContent>
                    </Select>
                  </div>
                  <div className="grid grid-cols-2 gap-3">
                    <div className="space-y-2"><Label>Expected qty</Label><Input type="number" min={1} required value={qty} onChange={(e) => setQty(e.target.value)} /></div>
                    <div className="space-y-2"><Label>Lot (optional)</Label><Input value={lot} onChange={(e) => setLot(e.target.value)} /></div>
                  </div>
                  <DialogFooter><Button type="submit" disabled={adding}>{adding && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}Add</Button></DialogFooter>
                </form>
              </DialogContent>
            </Dialog>
          </div>
          <div className="mt-3 border rounded-md">
            <Table>
              <TableHeader><TableRow><TableHead>SKU</TableHead><TableHead>Lot</TableHead><TableHead className="text-right">Expected</TableHead><TableHead className="text-right">Received</TableHead><TableHead></TableHead></TableRow></TableHeader>
              <TableBody>
                {lines && lines.length > 0 ? lines.map((l) => {
                  const remaining = l.expected_qty - l.received_qty;
                  return (
                    <TableRow key={l.id}>
                      <TableCell>{skuLabel(l.sku_id)}</TableCell>
                      <TableCell className="text-muted-foreground">{l.lot ?? "—"}</TableCell>
                      <TableCell className="text-right tabular-nums">{l.expected_qty}</TableCell>
                      <TableCell className="text-right tabular-nums">{l.received_qty}</TableCell>
                      <TableCell className="text-right">
                        <div className="flex items-center justify-end gap-2">
                          <Button size="sm" variant="ghost" onClick={() => {
                            setVarianceFor(l.id);
                            setVarianceReason(remaining > 0 ? "receiving_shortage" : "receiving_overage");
                            setVarianceQty(String(Math.max(1, Math.abs(remaining))));
                          }}>
                            <AlertTriangle className="h-4 w-4 mr-2" />Variance
                          </Button>
                          <Button size="touch" variant="outline" disabled={remaining <= 0} onClick={() => { setRecvFor(l.id); setRecvQty(String(remaining)); }}>
                            <PackageCheck className="h-4 w-4 mr-2" />Receive
                          </Button>
                        </div>
                      </TableCell>
                    </TableRow>
                  );
                }) : (<TableRow><TableCell colSpan={5} className="text-center py-8 text-muted-foreground">No lines yet.</TableCell></TableRow>)}
              </TableBody>
            </Table>
          </div>
        </CardContent>
      </Card>
      <PhotoSection asnLines={lines ?? []} skuLabel={skuLabel} tenantId={asn.tenant_id} />
      <Dialog open={!!recvFor} onOpenChange={(o) => { if (!o) setRecvFor(null); }}>
        <DialogContent>
          <DialogHeader><DialogTitle>Receive into location</DialogTitle></DialogHeader>
          <form onSubmit={receive} className="space-y-4">
            <div className="text-sm text-muted-foreground">{lineToReceive && skuLabel(lineToReceive.sku_id)}</div>
            {suggestedLoc?.location_id && (
              <div className="flex items-center gap-2 text-xs text-muted-foreground bg-muted/40 border rounded-md px-3 py-2">
                <Lightbulb className="h-3.5 w-3.5" />
                Suggested bin <span className="font-mono">{suggestedLoc.code}</span> · {suggestedLoc.reason}.
              </div>
            )}
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-2"><Label>Quantity</Label><Input type="number" min={1} required value={recvQty} onChange={(e) => setRecvQty(e.target.value)} /></div>
              <div className="space-y-2"><Label>Location</Label>
                <Select value={recvLoc} onValueChange={setRecvLoc}>
                  <SelectTrigger><SelectValue placeholder="Pick a bin" /></SelectTrigger>
                  <SelectContent>{locations?.map((l) => <SelectItem key={l.id} value={l.id}>{l.code} · {l.type}</SelectItem>)}</SelectContent>
                </Select>
              </div>
            </div>
            <div className="space-y-2"><Label>Expiry date (optional)</Label>
              <Input type="date" value={recvExpiry} onChange={(e) => setRecvExpiry(e.target.value)} />
            </div>
            {tracksSerials && (
              <div className="space-y-2">
                <Label className="flex items-center gap-2"><Hash className="h-3.5 w-3.5" />Serial numbers ({recvQty || 0} required)</Label>
                <Textarea
                  rows={5}
                  placeholder="One serial per line, or paste comma/space-separated"
                  value={recvSerials}
                  onChange={(e) => setRecvSerials(e.target.value)}
                  className="font-mono text-xs"
                />
                <p className="text-xs text-muted-foreground">Duplicates within this SKU are rejected automatically.</p>
              </div>
            )}
            <DialogFooter><Button size="touch" type="submit" disabled={receiving}>{receiving && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}Confirm receipt</Button></DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
      <Dialog open={!!varianceFor} onOpenChange={(o) => { if (!o) { setVarianceFor(null); setVarianceNotes(""); } }}>
        <DialogContent>
          <DialogHeader><DialogTitle>Record receiving variance</DialogTitle></DialogHeader>
          <form onSubmit={saveVariance} className="space-y-4">
            <div className="text-sm text-muted-foreground">
              {varianceLine && skuLabel(varianceLine.sku_id)}
              {varianceLine && (
                <div className="text-xs mt-1">
                  Expected {varianceLine.expected_qty} · Received {varianceLine.received_qty}
                </div>
              )}
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-2">
                <Label>Reason</Label>
                <Select value={varianceReason} onValueChange={(v) => setVarianceReason(v as "receiving_overage" | "receiving_shortage")}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="receiving_shortage">Shortage (less than expected)</SelectItem>
                    <SelectItem value="receiving_overage">Overage (more than expected)</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>Units</Label>
                <Input type="number" min={1} required value={varianceQty} onChange={(e) => setVarianceQty(e.target.value)} />
              </div>
            </div>
            <div className="space-y-2">
              <Label>Notes (required)</Label>
              <Textarea rows={3} required value={varianceNotes} onChange={(e) => setVarianceNotes(e.target.value)} placeholder="e.g. carton 3 of 5 short by 6 units, seal intact" />
              <p className="text-xs text-muted-foreground">Variances of 10+ units require supervisor approval before posting to inventory.</p>
            </div>
            <DialogFooter><Button type="submit" disabled={savingVariance}>{savingVariance && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}Record variance</Button></DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
      <div><Button variant="ghost" size="sm" onClick={() => navigate({ to: "/app/receiving" })}>← Back</Button></div>
    </div>
  );
}

function PhotoSection({ asnLines, skuLabel, tenantId }: { asnLines: { id: string; sku_id: string }[]; skuLabel: (id: string) => string; tenantId: string }) {
  const qc = useQueryClient();
  const listPhotosFn = useServerFn(listReceiptPhotos);
  const recordPhotoFn = useServerFn(recordReceiptPhoto);
  const deletePhotoFn = useServerFn(deleteReceiptPhoto);
  const lineIds = asnLines.map((l) => l.id);
  const { data: photos = [] } = useQuery({
    queryKey: ["asn-photos", lineIds.join(",")],
    enabled: lineIds.length > 0,
    queryFn: () => listPhotosFn({ data: { receiptLineIds: lineIds } }),
  });
  const [uploadFor, setUploadFor] = useState<string>("");
  const [uploading, setUploading] = useState(false);
  const onFile = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file || !uploadFor) return;
    setUploading(true);
    const ext = file.name.split(".").pop() || "jpg";
    const path = `${tenantId}/${uploadFor}/${crypto.randomUUID()}.${ext}`;
    // Binary upload stays on browser client (avoid relaying blobs through worker).
    const { error: upErr } = await supabase.storage.from("receipt-photos").upload(path, file, { contentType: file.type });
    if (upErr) { setUploading(false); return toast.error(upErr.message); }
    e.target.value = "";
    try {
      await runServerFn(
        () => recordPhotoFn({ data: { receiptLineId: uploadFor, tenantId, storagePath: path } }),
        "Save photo",
      );
    } catch { setUploading(false); return; }
    setUploading(false);
    toast.success("Photo uploaded");
    qc.invalidateQueries({ queryKey: ["asn-photos"] });
  };
  const removePhoto = async (id: string, path: string) => {
    try {
      await runServerFn(
        () => deletePhotoFn({ data: { id, storagePath: path } }),
        "Delete photo",
      );
    } catch { return; }
    qc.invalidateQueries({ queryKey: ["asn-photos"] });
  };
  if (lineIds.length === 0) return null;
  return (
    <Card>
      <CardHeader><CardTitle className="text-base flex items-center gap-2"><Camera className="h-4 w-4" />Receipt photos</CardTitle></CardHeader>
      <CardContent className="space-y-3">
        <div className="flex flex-wrap items-center gap-2">
          <Select value={uploadFor} onValueChange={setUploadFor}>
            <SelectTrigger className="w-72"><SelectValue placeholder="Attach photo to line…" /></SelectTrigger>
            <SelectContent>{asnLines.map((l) => <SelectItem key={l.id} value={l.id}>{skuLabel(l.sku_id)}</SelectItem>)}</SelectContent>
          </Select>
          <label className={`inline-flex items-center gap-2 text-sm border rounded-md px-3 py-1.5 cursor-pointer hover:bg-muted ${(!uploadFor || uploading) ? "opacity-50 pointer-events-none" : ""}`}>
            {uploading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Camera className="h-4 w-4" />}
            Upload
            <input type="file" accept="image/*" capture="environment" className="hidden" onChange={onFile} disabled={!uploadFor || uploading} />
          </label>
        </div>
        {photos.length > 0 ? (
          <div className="grid grid-cols-2 sm:grid-cols-4 md:grid-cols-6 gap-3">
            {photos.map((p) => (
              <div key={p.id} className="relative group border rounded-md overflow-hidden bg-muted">
                {p.url && <img src={p.url} alt={p.caption ?? ""} className="aspect-square object-cover w-full" />}
                <button onClick={() => removePhoto(p.id, p.storage_path)} className="absolute top-1 right-1 bg-background/80 backdrop-blur p-1 rounded opacity-0 group-hover:opacity-100 transition">
                  <Trash2 className="h-3 w-3 text-destructive" />
                </button>
              </div>
            ))}
          </div>
        ) : (
          <p className="text-xs text-muted-foreground">No photos yet. Use the camera to capture damage or proof of receipt.</p>
        )}
      </CardContent>
    </Card>
  );
}